<template>
  <section class="demo-section">
    <div class="demo-container">
      <div class="form-container">

        <!-- ===== PASO 1: email ===== -->
        <Transition name="slide-fade" mode="out-in">
          <div v-if="step === 1" key="step1">
            <div class="form-header">
              <h2 class="demo-title">
                ¿Quieres ver cómo <span class="highlight">FourinTrack</span> puede mejorar tu mantenimiento?
              </h2>
              <p class="demo-description">
                Solicita tu demo personalizada. Empieza solo con tu correo, te contactamos en menos de 24h.
              </p>
            </div>

            <form @submit.prevent="submitStep1" class="demo-form">
              <div class="form-group single">
                <label for="demo-email">Tu email de trabajo</label>
                <div class="email-row">
                  <input id="demo-email" type="email" v-model.trim="email" placeholder="tu@empresa.com"
                    :class="{ invalid: emailTouched && !emailValid }" @blur="emailTouched = true" />
                  <button type="submit" class="submit-button email-submit" :disabled="isSubmitting || !emailValid">
                    <span v-if="!isSubmitting">demo gratuita →</span>
                    <span v-else>Enviando...</span>
                    <div v-if="isSubmitting" class="loading-spinner"></div>
                  </button>
                </div>
                <span v-if="emailTouched && !emailValid" class="field-error">Ingresa un email válido.</span>
              </div>

              <div v-if="submitMessage" class="submit-message">{{ submitMessage }}</div>

              <div class="reassure">
                <div class="reassure-item">
                  <span class="material-symbols-outlined">money_off</span>
                  <span>Sin compromiso</span>
                </div>
                <div class="reassure-item">
                  <span class="material-symbols-outlined">bolt</span>
                  <span>Te contactamos en &lt;24h</span>
                </div>
                
              </div>
            </form>
          </div>

          <!-- ===== PASO 2: datos complementarios ===== -->
          <div v-else-if="step === 2" key="step2">
            <div class="form-header">
              <h2 class="demo-title">¡Perfecto! Cuéntanos un poco más</h2>
              <p class="demo-description">Para personalizar tu demo a tu operación.</p>
            </div>

            <form @submit.prevent="submitStep2" class="demo-form">
              <div class="form-row">
                <div class="form-column">
                  <div class="form-group">
                    <label for="demo-nombre">Tu nombre</label>
                    <input id="demo-nombre" type="text" v-model.trim="form.nombre" required placeholder="Tu nombre" />
                  </div>
                  <div class="form-group">
                    <label for="demo-empresa">Empresa</label>
                    <input id="demo-empresa" type="text" v-model.trim="form.empresa" required
                      placeholder="Nombre de tu empresa" />
                  </div>
                </div>
                <div class="form-column">
                  <div class="form-group">
                    <label for="demo-whatsapp">WhatsApp (opcional)</label>
                    <input id="demo-whatsapp" type="tel" v-model.trim="form.whatsapp" placeholder="+57 300 123 4567" />
                  </div>
                  <div class="form-group">
                    <label>¿Cuántos técnicos tiene tu equipo?</label>
                    <div class="team-options">
                      <label v-for="opt in teamSizes" :key="opt" class="team-option"
                        :class="{ selected: form.team_size === opt }">
                        <input type="radio" name="team_size" :value="opt" v-model="form.team_size" />
                        {{ opt }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <button type="submit" class="submit-button" :disabled="isSubmitting || !step2Valid">
                <span v-if="!isSubmitting">Confirmar solicitud →</span>
                <span v-else>Enviando...</span>
                <div v-if="isSubmitting" class="loading-spinner"></div>
              </button>

              <div v-if="submitMessage" class="submit-message">{{ submitMessage }}</div>
            </form>
          </div>

          <!-- ===== CONFIRMACIÓN ===== -->
          <div v-else key="done" class="confirmation">
            <div class="confirm-icon">
              <span class="material-symbols-outlined">check</span>
            </div>
            <h2 class="demo-title">¡Solicitud recibida!</h2>
            <p class="demo-description">
              Te contactaremos al email: <strong>{{ email }}</strong><br>
              Tiempo de respuesta: menos de 24 horas hábiles.
            </p>
            <p class="confirm-cta-label">Mientras tanto, ¿quieres explorar el producto?</p>
            <a :href="REGISTRO_URL" class="submit-button confirm-cta">Empieza gratis con F-Free →</a>
          </div>
        </Transition>

      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'

const props = withDefaults(defineProps<{ source?: string }>(), { source: 'home' })

// CTA de registro self-service aún no existe (dep FM-524). Fallback a la app.
const REGISTRO_URL = 'https://app.fourin.co/'

const teamSizes = ['1-5', '6-20', '21-50', '50+'] as const

const step = ref<1 | 2 | 3>(1)
const email = ref('')
const emailTouched = ref(false)
const requestToken = ref<string | null>(null)
const isSubmitting = ref(false)
const submitMessage = ref('')

const form = reactive({
  nombre: '',
  empresa: '',
  whatsapp: '',
  team_size: '',
})

const utm = reactive<{ utm_source: string | null; utm_campaign: string | null }>({
  utm_source: null,
  utm_campaign: null,
})

const emailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value))
const step2Valid = computed(() => form.nombre.length > 0 && form.empresa.length > 0)

onMounted(() => {
  try {
    const url = new URL(window.location.href)
    utm.utm_source = url.searchParams.get('utm_source')
    utm.utm_campaign = url.searchParams.get('utm_campaign')
    if (utm.utm_source) sessionStorage.setItem('utm_source', utm.utm_source)
    if (utm.utm_campaign) sessionStorage.setItem('utm_campaign', utm.utm_campaign)
  } catch (e) {}
})

const submitStep1 = async () => {
  emailTouched.value = true
  if (!emailValid.value) return
  isSubmitting.value = true
  submitMessage.value = ''
  try {
    const { createDemoRequestStep1 } = await import('../lib/supabase')
    const data = await createDemoRequestStep1({
      email: email.value,
      source: props.source,
      utm_source: utm.utm_source,
      utm_campaign: utm.utm_campaign,
    })
    requestToken.value = data.token
    step.value = 2
  } catch (error) {
    console.error(error)
    submitMessage.value = 'Hubo un error enviando tu solicitud. Intenta de nuevo.'
  } finally {
    isSubmitting.value = false
  }
}

const submitStep2 = async () => {
  if (!step2Valid.value || !requestToken.value) return
  isSubmitting.value = true
  submitMessage.value = ''
  try {
    const { updateDemoRequestStep2 } = await import('../lib/supabase')
    await updateDemoRequestStep2(requestToken.value, {
      nombre: form.nombre,
      empresa: form.empresa,
      whatsapp: form.whatsapp,
      team_size: form.team_size,
    })
    step.value = 3
  } catch (error) {
    console.error(error)
    submitMessage.value = 'Hubo un error completando tu solicitud. Intenta de nuevo.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.demo-section {
  padding: 60px 20px;
  background: #ffffff;
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 2px solid;
  border-image: linear-gradient(to right, #ffffff, #e2e8f0, #ffffff) 1;
  border-bottom: 2px solid;
}

.demo-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
}

.form-container {
  background: #ffffff;
  border-radius: 24px;
  padding: 50px;
  width: 100%;
  max-width: 1200px;
}

.form-header {
  margin-bottom: 40px;
}

.demo-title {
  font-size: 3rem;
  font-weight: 800;
  margin: 0 auto 10px auto;
  line-height: 1.1;
  color: #1a202c;
  text-align: center;
}

.demo-title .highlight {
  color: #129bef;
  font-weight: 700;
}

.demo-description {
  font-size: 20px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
  text-align: center;
}

.demo-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group.single {
  width: 100%;
  max-width: 680px;
}

.email-row {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.email-row input {
  flex: 1;
}

.email-submit {
  width: auto;
  max-width: none;
  margin-top: 0;
  height: 50px;
  padding: 0 24px;
  white-space: nowrap;
  flex: none;
}

.form-group label {
  color: #000000;
  font-weight: 600;
  font-size: 14px;
}

.form-group input,
.form-group select {
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  color: #000000;
  font-size: 16px;
  transition: all 0.3s ease;
  width: 100%;
  height: 50px;
}

.form-group.single input {
  min-width: unset;
}

.form-row .form-group input {
  min-width: 320px;
}

.form-group input::placeholder {
  color: #9ca3af;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #129bef;
  box-shadow: 0 0 0 3px rgba(18, 155, 239, 0.1);
}

.form-group input.invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.field-error {
  color: #dc2626;
  font-size: 13px;
  font-weight: 500;
}

/* Radios tamaño de equipo */
.team-options {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.team-option {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 4px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;
}

.team-option.selected {
  border-color: #129bef;
  background: #f4f9ff;
  color: #129bef;
}

.team-option input {
  display: none;
}

.submit-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #129bef;
  color: #ffffff;
  border: none;
  padding: 18px 32px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  max-width: 360px;
  text-decoration: none;
}

.submit-button:hover {
  background: #0ea5e9;
  transform: translateY(-2px);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.submit-button:disabled:hover {
  transform: none;
  box-shadow: none;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.submit-message {
  margin-top: 15px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #dc2626;
}

.reassure {
  display: flex;
  justify-content: center;
  gap: 42px;
  margin-top: 10px;
  color: #000000;
  font-size: 14px;
  font-weight: 600;
}

.reassure-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.reassure-item .material-symbols-outlined {
  color: #129bef;
  font-size: 28px;
}

/* Confirmación */
.confirmation {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.confirm-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #129bef;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-icon .material-symbols-outlined {
  color: #ffffff;
  font-size: 44px;
  font-weight: 700;
}

.confirm-cta-label {
  margin: 10px 0 0;
  color: #4b5563;
  font-weight: 600;
}

.confirm-cta {
  max-width: 360px;
}

/* Transición entre pasos */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Responsive */
@media (max-width: 968px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .demo-section {
    padding: 40px 20px;
  }
  .form-container {
    padding: 40px 30px;
  }
  .demo-title {
    font-size: 2rem;
  }
  .demo-description {
    font-size: 15px;
  }
  .form-row .form-group input {
    min-width: unset;
  }
  .reassure {
    flex-direction: column;
    gap: 6px;
    align-items: center;
  }

  .email-row {
    flex-direction: column;
  }

  .email-submit {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .form-container {
    padding: 30px 20px;
    border-radius: 16px;
  }
  .demo-title {
    font-size: 1.75rem;
  }
  .submit-button {
    padding: 16px 28px;
    font-size: 14px;
  }
}
</style>
